// Маршруты пульта (задача D4): разделы, роли, вход и роутер React Router 8 в режиме данных.
//
// - `ROUTES` — разделы в порядке меню до запуска; каркас D5 строит меню по ролям из этой же таблицы.
// - Корень: загрузчик узнаёт, кто вошёл (`meQuery` в кэш до первого экрана). Нет сессии — `/login?next=…`.
// - Разделы — ленивые куски сборки, у каждого свой `ErrorBoundary`. Роль не подходит — «Нет доступа»,
//   путь не найден — «Такой страницы в пульте нет». Пока экрана нет — заглушка.
import { ROLES, type Role } from '@contract/index.js';
import type { QueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  Bot,
  FlaskConical,
  Gauge,
  GraduationCap,
  Inbox,
  ListChecks,
  Mail,
  MessagesSquare,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { createBrowserRouter, redirect, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { ApiError } from '@/api/errors';
import { meQuery } from '@/api/queries/auth';
import { LoginPage } from '@/auth/login-page';
import { AppLayout, AppLoading } from './app-layout';
import { NotFound } from './not-found';
import { RootRouteError, RouteError } from './route-error';
import { SectionGate, type SectionHandle } from './section';

export type RouteId = 'readiness' | 'overview' | 'dialogs' | 'mail' | 'review' | 'lessons' | 'tests' | 'launch' | 'testbot' | 'help' | 'login';
export type SectionId = Exclude<RouteId, 'login'>;

interface RouteBase {
  /** Шаблон пути: `/review/:queue?`. */
  readonly path: string;
  /** Ссылка меню — путь без необязательных частей: `/review`. */
  readonly href: string;
  /** Кому раздел открыт; у входа — никому из вошедших (он для тех, кто не вошёл). */
  readonly roles: readonly Role[];
}

export interface SectionRoute extends RouteBase {
  readonly id: SectionId;
  readonly titleRu: string;
  readonly icon: LucideIcon;
}

export interface LoginRoute extends RouteBase {
  readonly id: 'login';
  readonly titleRu: null;
  readonly icon: null;
}

export type AppRoute = SectionRoute | LoginRoute;

const ADMIN_ONLY: readonly Role[] = ['admin'];
/** Уроки после закрытого диалога (план M, M8): предложения для базы и регламента разбирают админ и «Редактор базы». */
const LESSON_REVIEWERS: readonly Role[] = ['admin', 'editor'];

/** Разделы в порядке меню до запуска (первый — «Готовность»). */
export const ROUTES: readonly AppRoute[] = [
  { id: 'readiness', path: '/readiness', href: '/readiness', titleRu: 'Готовность', icon: ListChecks, roles: ROLES },
  { id: 'overview', path: '/', href: '/', titleRu: 'Обзор', icon: Gauge, roles: ROLES },
  { id: 'dialogs', path: '/dialogs', href: '/dialogs', titleRu: 'Диалоги', icon: MessagesSquare, roles: ROLES },
  // «Почта» (план MAIL, A9): письма отдела квалификации и что Анна с ними сделала; рядом с «Диалогами».
  { id: 'mail', path: '/mail', href: '/mail', titleRu: 'Почта', icon: Mail, roles: ROLES },
  { id: 'review', path: '/review/:queue?', href: '/review', titleRu: 'На проверку', icon: Inbox, roles: ADMIN_ONLY },
  { id: 'lessons', path: '/lessons', href: '/lessons', titleRu: 'Уроки', icon: GraduationCap, roles: LESSON_REVIEWERS },
  { id: 'tests', path: '/tests/runs', href: '/tests/runs', titleRu: 'Тесты', icon: FlaskConical, roles: ROLES },
  { id: 'launch', path: '/launch', href: '/launch', titleRu: 'Запуск', icon: SlidersHorizontal, roles: ROLES },
  // «Тест-бот» (T19): боты Telegram, на которых команда проверяет Анну; только админ.
  { id: 'testbot', path: '/testbot/:tab?', href: '/testbot', titleRu: 'Тест-бот', icon: Bot, roles: ADMIN_ONLY },
  { id: 'help', path: '/help/:doc?', href: '/help', titleRu: 'Справка', icon: BookOpen, roles: ROLES },
  { id: 'login', path: '/login', href: '/login', titleRu: null, icon: null, roles: [] },
];

function sectionOf(id: SectionId): SectionRoute {
  const route = ROUTES.find((r): r is SectionRoute => r.id === id);
  if (route === undefined) throw new Error(`В ROUTES нет раздела ${id}`);
  return route;
}

// ───────────────────────────── экраны разделов ─────────────────────────────

/** Экран раздела. Путь раздела может вести на несколько экранов: список диалогов и разбор диалога. */
export type ScreenId =
  | 'readiness'
  | 'overview'
  | 'dialogs'
  | 'dialog'
  | 'mail'
  | 'review'
  | 'lessons'
  | 'testRuns'
  | 'testRun'
  | 'launch'
  | 'testbot'
  | 'testbotChat'
  | 'help';

export interface ScreenModule {
  readonly Component: ComponentType;
}

/** Ленивые загрузчики экранов: задача экрана заменяет здесь заглушку своим модулем. */
export type ScreenLoaders = Readonly<Record<ScreenId, () => Promise<ScreenModule>>>;

const placeholder = async (): Promise<ScreenModule> => ({ Component: (await import('./placeholder')).Placeholder });

export const SCREENS: ScreenLoaders = {
  readiness: async () => ({ Component: (await import('@/features/readiness/readiness-page')).ReadinessPage }),
  overview: placeholder,
  dialogs: async () => ({ Component: (await import('@/features/dialogs/dialogs-page')).DialogsPage }),
  dialog: async () => ({ Component: (await import('@/features/dialog/dialog-page')).DialogPage }),
  mail: async () => ({ Component: (await import('@/features/mail/mail-page')).MailPage }),
  review: placeholder,
  lessons: async () => ({ Component: (await import('@/features/lessons/lessons-page')).LessonsPage }),
  // «Тесты» (план M, M2): пока — калибровка судьи; прогоны (D20) добавятся на этот же экран.
  testRuns: async () => ({ Component: (await import('@/features/tests/tests-page')).TestsPage }),
  testRun: placeholder,
  launch: async () => ({ Component: (await import('@/features/launch/launch-page')).LaunchPage }),
  testbot: async () => ({ Component: (await import('@/features/testbot/testbot-page')).TestbotPage }),
  testbotChat: async () => ({ Component: (await import('@/features/testbot/chat-page')).TestbotChatPage }),
  help: placeholder,
};

/** Пути экранов (относительно корня `/`); `null` — экран корня раздела `/` (index). */
const SCREEN_PATHS: readonly { readonly screen: ScreenId; readonly section: SectionId; readonly path: string | null }[] = [
  { screen: 'readiness', section: 'readiness', path: 'readiness' },
  { screen: 'overview', section: 'overview', path: null },
  { screen: 'dialogs', section: 'dialogs', path: 'dialogs' },
  { screen: 'dialog', section: 'dialogs', path: 'dialogs/:id' },
  { screen: 'mail', section: 'mail', path: 'mail' },
  { screen: 'review', section: 'review', path: 'review/:queue?' },
  { screen: 'lessons', section: 'lessons', path: 'lessons' },
  { screen: 'testRuns', section: 'tests', path: 'tests/runs' },
  { screen: 'testRun', section: 'tests', path: 'tests/runs/:id' },
  { screen: 'launch', section: 'launch', path: 'launch' },
  { screen: 'testbot', section: 'testbot', path: 'testbot/:tab?' },
  { screen: 'testbotChat', section: 'testbot', path: 'testbot/chat/:id' },
  { screen: 'help', section: 'help', path: 'help/:doc?' },
];

function screenRoute(screen: ScreenId, path: string | null, handle: SectionHandle, screens: ScreenLoaders): RouteObject {
  const load = screens[screen];
  // Кусок не загрузился — `lazy` всё равно выполняется, а ошибку бросает экран при отрисовке: её ловит ErrorBoundary
  // маршрута и показывает «Вышла новая версия пульта». Отказ самого `lazy` React Router 8.4 теряет: у маршрута без
  // загрузчика объектный `lazy: { Component }` ошибку проглатывает, а после отказа функции маршрут остаётся «ленивым»,
  // и при первой загрузке страницы вместо экрана — пустая панель (проверено тестом «ленивый кусок не загрузился»).
  const lazy = async (): Promise<{ Component: ComponentType }> => {
    try {
      return { Component: (await load()).Component };
    } catch (error) {
      return {
        Component: () => {
          throw error;
        },
      };
    }
  };
  return path === null
    ? { index: true, handle, ErrorBoundary: RouteError, lazy }
    : { path, handle, ErrorBoundary: RouteError, lazy };
}

/** Разделы: на каждый — проверка роли (`SectionGate`) и ленивые экраны внутри. */
function sectionRoutes(screens: ScreenLoaders): RouteObject[] {
  const sections = new Map<SectionId, RouteObject[]>();
  for (const { screen, section, path } of SCREEN_PATHS) {
    const handle: SectionHandle = { section: sectionOf(section), screen };
    const children = sections.get(section) ?? [];
    children.push(screenRoute(screen, path, handle, screens));
    sections.set(section, children);
  }
  return [...sections].map(([section, children]) => ({
    id: `section:${section}`,
    handle: { section: sectionOf(section) } satisfies SectionHandle,
    Component: SectionGate,
    ErrorBoundary: RouteError,
    children,
  }));
}

// ───────────────────────────── корень и вход ─────────────────────────────

/**
 * Корневой загрузчик: кто вошёл. 401 — на вход с `next`. Окна «Сессия истекла» там нет (оно живёт в корне пульта),
 * а событие сессии, которое поднял этот 401, закроет вход (`useLogin`).
 */
function rootLoader(queryClient: QueryClient) {
  return async ({ url }: LoaderFunctionArgs): Promise<null> => {
    try {
      await queryClient.ensureQueryData(meQuery);
      return null;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        throw redirect(`/login?next=${encodeURIComponent(`${url.pathname}${url.search}`)}`);
      }
      throw error;
    }
  };
}

/** Вход и корень пульта вокруг маршрутов разделов `sections` (тесты ставят сюда свой экран). */
export function buildAppRoutes(queryClient: QueryClient, sections: RouteObject[]): RouteObject[] {
  return [
    { path: '/login', Component: LoginPage, ErrorBoundary: RootRouteError },
    {
      id: 'root',
      path: '/',
      loader: rootLoader(queryClient),
      Component: AppLayout,
      HydrateFallback: AppLoading,
      ErrorBoundary: RootRouteError,
      children: sections,
    },
  ];
}

export interface AppRouterOptions {
  /** Подмена ленивых экранов (тесты): то, чего нет, берётся из `SCREENS`. */
  readonly screens?: Partial<ScreenLoaders>;
}

export function createAppRouter(queryClient: QueryClient, o: AppRouterOptions = {}) {
  const screens: ScreenLoaders = { ...SCREENS, ...o.screens };
  return createBrowserRouter(
    buildAppRoutes(queryClient, [
      ...sectionRoutes(screens),
      { path: 'tests', loader: () => redirect('/tests/runs') },
      { path: '*', Component: NotFound },
    ]),
  );
}
